import React, { useState } from 'react';
import { Box, Button, Stack, Typography, Paper, IconButton, CircularProgress, Divider } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { HttpService } from 'core/services/http';
import { CVHttpService } from 'features/feature_cv/services';
import { Utils } from 'core/utils';
import ExperienceFormDialog from 'features/feature_cv/components/dialogs/ExperienceFormDialog';

const formatText = (value, fallback = '—') => {
    if (!Utils.normalizeText) return value ?? fallback;
    return Utils.normalizeText(value, fallback);
};

const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
    } catch {
        return dateString;
    }
};

function ExperiencesSection({ cvId }) {
    const queryClient = useQueryClient();
    const [openDialog, setOpenDialog] = useState(false);
    const [editingExperience, setEditingExperience] = useState(null);

    const { data, isLoading, refetch } = useQuery(
        ['cv_experiences', cvId],
        async () => {
            const service = new HttpService();
            const response = await service.get(`/job/cv/jobexperience/?cv=${cvId}`, null);
            return Array.isArray(response) ? response : (response?.results || []);
        },
        { enabled: !!cvId }
    );

    const deleteMutation = useMutation(
        (id) => CVHttpService.deleteJobExperience(id),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['cv_experiences', cvId]);
            }
        }
    );

    const experiences = data || [];

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this experience?')) return;
        deleteMutation.mutate(id);
    };

    const handleSave = async () => {
        await refetch();
        setOpenDialog(false);
        setEditingExperience(null);
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    return (
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight={600}>
                    Work Experience ({experiences.length})
                </Typography>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                        setEditingExperience(null);
                        setOpenDialog(true);
                    }}
                >
                    Add Experience
                </Button>
            </Stack>
            <Divider sx={{ mb: 2 }} />

            {experiences.length === 0 ? (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                    No work experience added yet.
                </Typography>
            ) : (
                <Stack spacing={2}>
                    {experiences.map((exp) => (
                        <Paper key={exp.id} variant="outlined" sx={{ p: 2 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="start">
                                <Box sx={{ flexGrow: 1 }}>
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        {formatText(exp.title)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {formatText(exp.company_title)}
                                    </Typography>
                                    {exp.start_date || exp.end_date ? (
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                            {formatDate(exp.start_date)} - {exp.end_date ? formatDate(exp.end_date) : 'Present'}
                                        </Typography>
                                    ) : null}
                                    {exp.description && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                            {formatText(exp.description).substring(0, 150)}
                                            {exp.description.length > 150 ? '...' : ''}
                                        </Typography>
                                    )}
                                </Box>
                                <Stack direction="row" spacing={0.5}>
                                    <IconButton color="primary"
                                        size="small"
                                        onClick={() => {
                                            setEditingExperience(exp);
                                            setOpenDialog(true);
                                        }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleDelete(exp.id)}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                            </Stack>
                        </Paper>
                    ))}
                </Stack>
            )}

            <ExperienceFormDialog
                open={openDialog}
                onClose={() => {
                    setOpenDialog(false);
                    setEditingExperience(null);
                }}
                onSave={handleSave}
                cvId={cvId}
                experience={editingExperience}
            />
        </Paper>
    );
}

export default ExperiencesSection;

